/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { forwardRef, useEffect, useImperativeHandle, useState, useRef } from "react";
import { Slider, Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";

export interface SliderLimits {
  enabled?: boolean;
  minStart?: number;
  minEnd?: number;
  maxStart?: number;
  maxEnd?: number;
}

export interface SliderComponentProps {
  id?: string;
  value?: number | number[];
  min?: number;
  max?: number;
  step?: number;
  type?: "Default" | "MinRange" | "Range";
  orientation?: "Horizontal" | "Vertical";
  tooltip?: {
    isVisible?: boolean;
    placement?: "Before" | "After";
    showOn?: "Always" | "Auto" | "Hover" | "Focus";
    format?: string;
  };
  ticks?: {
    placement?: "Before" | "After" | "Both" | "None";
    largeStep?: number;
    smallStep?: number;
    showSmallTicks?: boolean;
  };
  limits?: SliderLimits;
  showButtons?: boolean;
  enabled?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  cssClass?: string;
  className?: string;
  width?: number | string;
  // Style customization
  color?: string;
  gradientColor1?: string;
  gradientColor2?: string;
  handleShadowColor?: string;
  handleGradientColor1?: string;
  handleGradientColor2?: string;
  // Callbacks - Syncfusion compatible
  change?: (args: { value: number | number[] }) => void;
  changed?: (args: { value: number | number[] }) => void;
  created?: () => void;
  // Style props
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
}

export interface SliderComponentRef {
  value: number | number[];
  refresh: () => void;
}

const SliderComponent = forwardRef<SliderComponentRef, SliderComponentProps>((props, ref) => {
  const {
    id,
    value: propValue,
    min = 0,
    max = 100,
    step = 1,
    type = "Default",
    orientation = "Horizontal",
    tooltip,
    enabled = true,
    disabled: disabledProp,
    readOnly,
    cssClass,
    className,
    width,
    color = "#6BBBE3",
    gradientColor1,
    gradientColor2,
    handleGradientColor1,
    handleGradientColor2,
    change,
    changed,
    created,
    style,
    sx,
    // limits is a Syncfusion concept not applicable to MUI Slider; extracted to prevent spread
    limits: _limits,
  } = props;

  const getDefaultValue = (): number | number[] => {
    if (propValue !== undefined) return propValue;
    if (type === "Range") return [min, max];
    return min;
  };

  const [value, setValue] = useState<number | number[]>(getDefaultValue());
  const sliderRef = useRef<HTMLSpanElement>(null);

  const isDisabled = disabledProp ?? !enabled;
  const isVertical = orientation === "Vertical";

  useEffect(() => {
    if (created) {
      created();
    }
  }, []);

  useEffect(() => {
    if (propValue !== undefined) {
      setValue(propValue);
    }
  }, [propValue]);

  useImperativeHandle(ref, () => ({
    get value() {
      return value;
    },
    set value(val: number | number[]) {
      setValue(val);
    },
    refresh: () => {
      // Force re-render
      setValue((prev) => (Array.isArray(prev) ? [...prev] : prev));
    },
  }));

  const handleChange = (_event: Event, newValue: number | number[]) => {
    if (readOnly) return;
    setValue(newValue);
    if (change) change({ value: newValue });
  };

  const handleChangeCommitted = (_event: React.SyntheticEvent | Event, newValue: number | number[]) => {
    if (changed) changed({ value: newValue });
  };

  // Compute gradient color for track
  const trackColor =
    gradientColor1 && gradientColor2
      ? `linear-gradient(90deg, ${gradientColor1}, ${gradientColor2})`
      : color || "#6BBBE3";

  const thumbColor =
    handleGradientColor1 && handleGradientColor2
      ? `linear-gradient(135deg, ${handleGradientColor1}, ${handleGradientColor2})`
      : color || "#6BBBE3";

  // Configure marks for ticks if needed — only when ticks prop is explicitly provided
  const ticks = props.ticks;
  const marks =
    ticks && ticks.placement !== "None"
      ? Array.from({ length: Math.floor((max - min) / (ticks.largeStep || step)) + 1 }, (_, i) => ({
          value: min + i * (ticks.largeStep || step),
          label: undefined,
        }))
      : false;

  return (
    <Box
      id={id}
      className={className || cssClass}
      style={{
        width: isVertical ? "auto" : width || "100%",
        height: isVertical ? width || "12.5rem" : "auto",
        ...style,
      }}
      sx={{ padding: "0.5rem", ...sx }}
    >
      <Slider
        ref={sliderRef}
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={isDisabled}
        orientation={isVertical ? "vertical" : "horizontal"}
        onChange={handleChange}
        onChangeCommitted={handleChangeCommitted}
        valueLabelDisplay={tooltip?.isVisible ? (tooltip.showOn === "Always" ? "on" : "auto") : "off"}
        marks={marks}
        sx={{
          color: color || "#6BBBE3",
          "& .MuiSlider-track": {
            background: trackColor,
            border: "none",
          },
          "& .MuiSlider-rail": {
            backgroundColor: "#e0e0e0",
          },
          "& .MuiSlider-thumb": {
            background: thumbColor,
            width: "1rem",
            height: "1rem",
            "&:hover, &.Mui-focusVisible": {
              boxShadow: `0 0 0 0.5rem ${color}33`,
            },
            "&.Mui-active": {
              boxShadow: `0 0 0 0.75rem ${color}33`,
            },
          },
          "& .MuiSlider-valueLabel": {
            backgroundColor: color || "#6BBBE3",
          },
          "& .MuiSlider-mark": {
            backgroundColor: "#bfbfbf",
          },
          "& .MuiSlider-markActive": {
            backgroundColor: "currentColor",
          },
        }}
      />
    </Box>
  );
});

SliderComponent.displayName = "SliderComponent";

export default SliderComponent;
